/**
 * Serviço global para gerenciamento de timeouts do Corel
 *
 * Carrega timeouts da API e fornece cache para uso em toda a aplicação.
 * Mantém fallback para valores hardcoded quando API não está disponível.
 */

import { apiWithETag, type APIWithETagResult } from "../api";
import { ACTION_TIMEOUTS, DEFAULT_FALLBACK_TIMEOUT_MS } from "./constants";
import { sendToTerminal } from "../terminalLogger";
import { LogCategory } from "../../config/logging";
import { TokenService } from "../tokenService";

/**
 * Estrutura de dados de timeout da API
 */
interface TimeoutConfigItem {
	value: number;
	default: number;
	description: string;
}

interface TimeoutConfigData {
	[category: string]: {
		[key: string]: TimeoutConfigItem;
	};
}

/**
 * Serviço singleton para gerenciamento de timeouts
 */
class TimeoutService {
	private static instance: TimeoutService;
	private cache: Map<string, number> = new Map();
	private configData: TimeoutConfigData | null = null;
	private loading: boolean = false;
	private loaded: boolean = false;
	private etag: string | null = null;
	private loadPromise: Promise<void> | null = null;
	private lastAccessTokenLoaded: string | null = null;
	private authLoadTimer: ReturnType<typeof setTimeout> | null = null;

	private constructor() {
		// Ouvir evento de autenticação para recarregar timeouts quando usuário fizer login
		// ou quando o access token realmente mudar. Evita ruído no bootstrap.
		if (typeof window !== "undefined") {
			window.addEventListener("auth:updated", ((event: CustomEvent) => {
				const detail = event.detail as { accessToken?: string | null } | undefined;
				if (!detail || !("accessToken" in detail)) {
					return;
				}

				const token = detail.accessToken ?? TokenService.getAccessToken();
				if (!token) {
					this.lastAccessTokenLoaded = null;
					return;
				}

				if (this.lastAccessTokenLoaded === token && this.loaded) {
					return;
				}

				if (this.authLoadTimer) {
					clearTimeout(this.authLoadTimer);
				}

				// Aguardar um pequeno settle time para deixar userData/PermissionContext
				// estabilizarem antes de carregar timeouts da API.
				this.authLoadTimer = setTimeout(() => {
					sendToTerminal(
						"🔄 [TIMEOUT_SERVICE] Access token detectado, carregando timeouts",
						{},
						LogCategory.BUSINESS,
					);
					this.loadTimeouts(true, token).catch(() => {
						// Erro já foi logado
					});
				}, 300);
			}) as EventListener);
		}
	}

	/**
	 * Obtém instância singleton
	 */
	public static getInstance(): TimeoutService {
		if (!TimeoutService.instance) {
			TimeoutService.instance = new TimeoutService();
		}
		return TimeoutService.instance;
	}

	/**
	 * Carrega timeouts da API
	 */
	public async loadTimeouts(
		force = false,
		tokenOverride?: string | null,
	): Promise<void> {
		// Verificar se usuário está autenticado antes de tentar carregar
		const token = tokenOverride ?? TokenService.getAccessToken();
		if (!token) {
			if (force) {
				sendToTerminal(
					"⏭️ [TIMEOUT_SERVICE] Usuário não autenticado, usando timeouts padrão",
					{
						willUseFallback: true,
					},
					LogCategory.BUSINESS,
				);
			}
			this.loaded = false;
			this.lastAccessTokenLoaded = null;
			return;
		}

		// Se já está carregando, retornar a promise existente
		if (this.loadPromise && !force) {
			return this.loadPromise;
		}

		// Se já carregou e não é forçado, retornar imediatamente
		if (this.loaded && !force) {
			return;
		}

		// Evitar múltiplas chamadas simultâneas
		if (this.loading && !force) {
			return;
		}

		this.loading = true;
		const API_ROUTE = "/config/corel-timeouts";

		const requestToken = token;
		this.loadPromise = (async () => {
			try {
				sendToTerminal(
					"📤 [TIMEOUT_SERVICE] Carregando timeouts da API",
					{
						route: API_ROUTE,
						force,
						hasETag: !!this.etag,
						hasToken: !!requestToken,
					},
					LogCategory.BUSINESS,
				);

				// Usar apiWithETag para suportar cache
				const response: APIWithETagResult = await apiWithETag(
					API_ROUTE,
					{ method: "GET" },
					{
						ifNoneMatch: this.etag || undefined,
						authTokenOverride: requestToken,
					},
				);

				// Tratar resposta 304 (Not Modified)
				if (response.notModified || response.status === 304) {
					sendToTerminal(
						"💾 [TIMEOUT_SERVICE] Cache válido (304 Not Modified)",
						{
							etag: this.etag,
						},
						LogCategory.BUSINESS,
					);
					this.loading = false;
					return;
				}

				const result = response.data;

				if (!result) {
					throw new Error("Resposta vazia da API");
				}

				// Guardar novo ETag se disponível
				if (response.etag) {
					this.etag = response.etag;
				} else if (result.version) {
					this.etag = result.version;
				}

				// Processar resposta
				const responseData = result.data || result;
				const isSuccess = result.success !== false;

				if (isSuccess && responseData && typeof responseData === "object") {
					this.configData = responseData;
					this.cache.clear();

					// Popular cache com valores da API
					Object.entries(responseData).forEach(([category, categoryData]) => {
						if (categoryData && typeof categoryData === "object") {
							Object.entries(categoryData).forEach(([key, item]) => {
								if (item && typeof item === "object" && "value" in item) {
									const fullKey = `${category}.${key}`;
									this.cache.set(fullKey, item.value as number);
								}
							});
						}
					});

					const totalConfigs = this.cache.size;
					this.loaded = true;
					this.lastAccessTokenLoaded = requestToken;

					sendToTerminal(
						"✅ [TIMEOUT_SERVICE] Timeouts carregados",
						{
							totalConfigs,
							categories: Object.keys(responseData),
							cacheSize: this.cache.size,
						},
						LogCategory.BUSINESS,
					);
				} else {
					throw new Error(
						result?.message || result?.error || "Formato de resposta inválido",
					);
				}
			} catch (error: any) {
				const errorMessage =
					error instanceof Error ? error.message : "Erro ao carregar timeouts";
				const errorStatus = error?.status || error?.response?.status;

				// Tratar 403 de forma mais elegante (pode ser backend não implementado ou sem permissão)
				if (errorStatus === 403) {
					sendToTerminal(
						"⚠️ [TIMEOUT_SERVICE] Acesso negado (403) - usando timeouts padrão",
						{
							error: errorMessage,
							status: errorStatus,
							reason:
								"Backend pode não estar implementado ou usuário sem permissão",
							willUseFallback: true,
						},
						LogCategory.BUSINESS,
					);
				} else if (errorStatus === 401) {
					sendToTerminal(
						"⚠️ [TIMEOUT_SERVICE] Não autenticado (401) - usando timeouts padrão",
						{
							error: errorMessage,
							status: errorStatus,
							willUseFallback: true,
						},
						LogCategory.BUSINESS,
					);
				} else {
					sendToTerminal(
						"❌ [TIMEOUT_SERVICE] Erro ao carregar timeouts",
						{
							error: errorMessage,
							status: errorStatus,
							willUseFallback: true,
						},
						LogCategory.ERROR,
					);
				}

				// Não lançar erro - usar fallback
				this.loaded = false;
				this.lastAccessTokenLoaded = null;
			} finally {
				this.loading = false;
				this.loadPromise = null;
			}
		})();

		return this.loadPromise;
	}

	/**
	 * Obtém timeout para uma action específica
	 *
	 * Ordem de prioridade:
	 * 1. Timeout da API/painel admin (cache)
	 * 2. ACTION_TIMEOUTS[action] (fallback por action = 1h)
	 * 3. DEFAULT_FALLBACK_TIMEOUT_MS (fallback global = 1h)
	 *
	 * @param action Nome da action (ex: 'abrir-arquivo', 'monitorar-objetos')
	 * @returns Timeout em milissegundos
	 */
	public getTimeout(action: string): number {
		// Tentar buscar da API primeiro (chave: action.{nome-action})
		const apiKey = `action.${action}`;
		if (this.cache.has(apiKey)) {
			const timeout = this.cache.get(apiKey)!;
			return timeout;
		}

		// Fallback para ACTION_TIMEOUTS (todos são 1h)
		if (ACTION_TIMEOUTS[action]) {
			return ACTION_TIMEOUTS[action];
		}

		// Fallback global (1 hora)
		return DEFAULT_FALLBACK_TIMEOUT_MS;
	}

	/**
	 * Obtém timeout para uma chave completa (ex: 'action.abrir-arquivo', 'connection.validationTimeoutMs')
	 *
	 * @param fullKey Chave completa no formato 'categoria.chave'
	 * @returns Timeout em milissegundos ou null se não encontrado
	 */
	public getTimeoutByKey(fullKey: string): number | null {
		if (this.cache.has(fullKey)) {
			return this.cache.get(fullKey)!;
		}
		return null;
	}

	/**
	 * Verifica se os timeouts foram carregados
	 */
	public isLoaded(): boolean {
		return this.loaded;
	}

	/**
	 * Verifica se está carregando
	 */
	public isLoading(): boolean {
		return this.loading;
	}

	/**
	 * Invalida cache e força recarregamento
	 */
	public invalidateCache(): void {
		this.etag = null;
		this.cache.clear();
		this.configData = null;
		this.loaded = false;
		this.lastAccessTokenLoaded = null;
		sendToTerminal(
			"🔄 [TIMEOUT_SERVICE] Cache invalidado",
			{},
			LogCategory.BUSINESS,
		);
	}

	/**
	 * Atualiza um timeout no cache (após salvar no painel admin)
	 */
	public updateTimeout(fullKey: string, value: number): void {
		this.cache.set(fullKey, value);

		// Atualizar também no configData se existir
		if (this.configData) {
			const [category, ...keyParts] = fullKey.split(".");
			const key = keyParts.join(".");

			if (this.configData[category] && this.configData[category][key]) {
				this.configData[category][key] = {
					...this.configData[category][key],
					value,
				};
			}
		}

		sendToTerminal(
			"💾 [TIMEOUT_SERVICE] Timeout atualizado no cache",
			{
				key: fullKey,
				value,
			},
			LogCategory.BUSINESS,
		);
	}

	/**
	 * Obtém todos os timeouts carregados
	 */
	public getAllTimeouts(): Map<string, number> {
		return new Map(this.cache);
	}

	/**
	 * Obtém dados completos da configuração
	 */
	public getConfigData(): TimeoutConfigData | null {
		return this.configData;
	}
}

// Exportar instância singleton
export const timeoutService = TimeoutService.getInstance();

// Exportar função helper para uso direto
export function getActionTimeout(action: string): number {
	return timeoutService.getTimeout(action);
}
